import React from 'react';
import PropTypes from 'prop-types';
import Box from '@material-ui/core/Box';
import Typography from '@material-ui/core/Typography';
import Link from '@material-ui/core/Link';
import Paper from '@material-ui/core/Paper';
import Table from '@material-ui/core/Table';
import TableBody from '@material-ui/core/TableBody';
import TableCell from '@material-ui/core/TableCell';
import TableContainer from '@material-ui/core/TableContainer';
import TableHead from '@material-ui/core/TableHead';
import TableRow from '@material-ui/core/TableRow';
import Button from '@material-ui/core/Button';
import Divider from '@material-ui/core/Divider';
import { useTheme } from '@material-ui/core/styles';
import Container from 'common/Container';
import PriceVatDisclaimer from 'common/PriceVatDisclaimer';
import handleOpenWhatsApp from 'services/whatsappService';
import { enProduccionConMemForegroundOnPage } from './enProduccionConMemReadableText';
import { EN_PRODUCCION_CON_MEM_LOGO_SRC } from './brandAssets';
import { ROLDA_PROPOSAL } from './roldaProposalContent';
import EnProduccionConMemProposalPlanCards from './components/EnProduccionConMemProposalPlanCards';

const LandingRoldaEnProduccionConMem = ({ themeMode = 'light' }) => {
  const theme = useTheme();
  const headingColor = enProduccionConMemForegroundOnPage(theme);
  const accent = theme.palette.secondary.main;
  const p = ROLDA_PROPOSAL;

  return (
    <Box sx={{ pb: 8, pt: { xs: 2, md: 3 } }}>
      <Container>
        <Box sx={{ mb: 3 }}>
          <Link
            href="/enproduccionconmem"
            underline="hover"
            sx={{ color: headingColor, fontWeight: 600, '&:hover': { color: accent } }}
          >
            ← Media kit En Producción con MEM
          </Link>
        </Box>

        <Box
          sx={{
            display: 'flex',
            flexDirection: { xs: 'column', sm: 'row' },
            alignItems: { xs: 'flex-start', sm: 'center' },
            gap: 3,
            mb: 4,
          }}
        >
          <Box
            component="img"
            src={EN_PRODUCCION_CON_MEM_LOGO_SRC}
            alt="En Producción con MEM"
            sx={{
              maxWidth: 220,
              width: '100%',
              height: 'auto',
              objectFit: 'contain',
              bgcolor: themeMode === 'dark' ? 'rgba(255,255,255,0.06)' : '#fff',
              p: 1.5,
              borderRadius: 1,
              border: `2px solid ${accent}`,
            }}
          />
          <Box>
            <Typography variant="overline" sx={{ letterSpacing: 1.2, color: accent, fontWeight: 700 }}>
              {p.eyebrow}
            </Typography>
            <Typography
              variant="h3"
              component="h1"
              sx={{
                fontFamily: 'Georgia, "Times New Roman", serif',
                fontWeight: 700,
                color: headingColor,
                lineHeight: 1.2,
                mb: 1,
              }}
            >
              {p.title}
            </Typography>
            <Typography
              variant="h5"
              sx={{
                fontFamily: 'Georgia, "Times New Roman", serif',
                fontStyle: 'italic',
                color: headingColor,
                fontWeight: 600,
                opacity: 0.95,
              }}
            >
              {p.concept}
            </Typography>
          </Box>
        </Box>

        <Divider sx={{ mb: 4, borderColor: accent, opacity: 0.35 }} />

        <Typography variant="body1" color="textSecondary" sx={{ mb: 4, maxWidth: 800, lineHeight: 1.75 }}>
          {p.intro}
        </Typography>

        <EnProduccionConMemProposalPlanCards
          themeMode={themeMode}
          overline={p.planExplainSection.overline}
          title={p.planExplainSection.title}
          subtitle={p.planExplainSection.subtitle}
          body={p.planExplainSection.body}
          cards={p.planExplainCards}
        />

        <Box component="section" sx={{ mb: 6 }}>
          <Typography
            variant="h4"
            component="h2"
            gutterBottom
            sx={{ fontFamily: 'Georgia, "Times New Roman", serif', color: headingColor, fontWeight: 700 }}
          >
            {p.planTableHeading}
          </Typography>
          <Typography variant="subtitle1" color="textSecondary" sx={{ mb: 2, fontWeight: 600 }}>
            {p.planTitle}
          </Typography>
          <TableContainer
            component={Paper}
            elevation={0}
            sx={{
              width: '100%',
              maxWidth: { xs: '100%', md: 960 },
              mx: 'auto',
              border: `1px solid ${theme.palette.divider}`,
              borderRadius: 1,
              overflowX: 'auto',
            }}
          >
            <Table size="small" aria-label="Plan mensual Rolda × En Producción con MEM">
              <TableHead>
                <TableRow>
                  <TableCell sx={{ fontWeight: 700, color: headingColor, minWidth: 120 }}>
                    Servicio estratégico
                  </TableCell>
                  <TableCell sx={{ fontWeight: 700, color: headingColor, minWidth: 200 }}>
                    Detalle del alcance
                  </TableCell>
                  <TableCell sx={{ fontWeight: 700, color: headingColor, minWidth: 160 }}>
                    Valor agregado
                  </TableCell>
                  <TableCell align="right" sx={{ fontWeight: 700, color: headingColor, whiteSpace: 'nowrap' }}>
                    Inversión
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {p.planRows.map((row) => (
                  <TableRow key={row.service}>
                    <TableCell sx={{ fontWeight: 600, verticalAlign: 'top' }}>{row.service}</TableCell>
                    <TableCell sx={{ color: 'text.secondary', verticalAlign: 'top', lineHeight: 1.65 }}>
                      {row.detail}
                    </TableCell>
                    <TableCell sx={{ color: 'text.secondary', verticalAlign: 'top', lineHeight: 1.65 }}>
                      {row.valueAdded}
                    </TableCell>
                    <TableCell align="right" sx={{ fontWeight: 700, verticalAlign: 'top', whiteSpace: 'nowrap' }}>
                      {row.amount}
                    </TableCell>
                  </TableRow>
                ))}
                <TableRow>
                  <TableCell colSpan={3} sx={{ fontWeight: 800, color: headingColor }}>
                    Total mensual — {p.planTotalLabel}
                  </TableCell>
                  <TableCell align="right" sx={{ fontWeight: 800, color: accent, fontSize: '1.1rem' }}>
                    {p.planTotalAmount}
                  </TableCell>
                </TableRow>
              </TableBody>
            </Table>
          </TableContainer>
          <Typography variant="caption" color="textSecondary" sx={{ display: 'block', mt: 1.5, textAlign: 'center' }}>
            Montos orientativos en USD al mes, según propuesta comercial.
          </Typography>
          <PriceVatDisclaimer />
        </Box>

        <Paper
          elevation={0}
          sx={{
            p: { xs: 3, md: 4 },
            textAlign: 'center',
            bgcolor: themeMode === 'dark' ? 'rgba(255,255,255,0.06)' : 'rgba(0,0,0,0.03)',
            border: `1px solid ${theme.palette.divider}`,
          }}
        >
          <Typography variant="h5" sx={{ fontWeight: 700, color: headingColor, mb: 2 }}>
            ¿Activamos el plan de autoridad y estilo para Rolda con En Producción con MEM?
          </Typography>
          <Typography variant="body2" color="textSecondary" sx={{ mb: 3, maxWidth: 560, mx: 'auto' }}>
            Escríbenos por WhatsApp o correo; referencia esta propuesta y coordinamos el siguiente paso con el equipo
            comercial TNO Network.
          </Typography>
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, justifyContent: 'center' }}>
            <Button variant="contained" color="secondary" size="large" onClick={() => handleOpenWhatsApp()}>
              WhatsApp comercial
            </Button>
            <Button
              variant="outlined"
              color="secondary"
              size="large"
              component={Link}
              href="mailto:mercadeoyventas@tnonetwork.com?subject=Propuesta%20Rolda%20x%20En%20Producci%C3%B3n%20con%20MEM"
            >
              mercadeoyventas@tnonetwork.com
            </Button>
          </Box>
        </Paper>
      </Container>
    </Box>
  );
};

LandingRoldaEnProduccionConMem.propTypes = {
  themeMode: PropTypes.string,
};

export default LandingRoldaEnProduccionConMem;
